import { useQuery } from '@tanstack/react-query';
import {
    CityBrowsePlace,
    CityBrowseResponseBody,
    getNearbyCities,
} from '../../../../api/nearbyCities';
import { useRouteContext } from '../../../../context';
import { NearbyCity } from '../../../../shared/nearbyCities/NearbyCities';
import { useLocation } from './location';

/**
 * Transforms the URLs returned from the nearby cities endpoint into the appropriate category browse url
 *
 * @param currentPath The current path for the page
 * @param cityBrowseUrl Url for a nearby city from the city browse endpoint
 */
function transformCityBrowseUrl(
    currentPath: string,
    cityBrowseUrl: string,
): string {
    const locationSlug = cityBrowseUrl.match(/\/d\/([^\/]+)\/.*/)?.[1] || '';
    const pathParts = currentPath
        .split('/')
        .filter((pathPart) => pathPart.length);
    pathParts[1] = locationSlug;
    return `/${pathParts.join('/')}/`;
}

const transformResponse = (
    currentPath: string,
    response: CityBrowseResponseBody,
): NearbyCity[] | undefined =>
    response?.buckets?.[0]?.places?.map(
        ({ id, url, name, subhead }: CityBrowsePlace): NearbyCity => {
            return {
                id,
                name,
                distance: subhead,
                url: transformCityBrowseUrl(currentPath, url),
            };
        },
    );

/**
 * Calls the city browse endpoint with a single bucket, 'nearby_cities,' to retrieve cities nearby.
 *
 * @returns NearbyCity[]
 */
export function useNearbyCities(): NearbyCity[] {
    const { data: location } = useLocation();
    const routeInfo = useRouteContext();

    const { data: nearbyCitiesList } = useQuery(
        ['category-browse', 'get-nearby-cities', location?.placeId],
        async () => await getNearbyCities(location?.placeId),
        {
            select: (response) =>
                transformResponse(routeInfo.match.url, response),
            enabled: !!location,
        },
    );

    return nearbyCitiesList?.length ? nearbyCitiesList : [];
}
